import Axios from '@/utils/axios';
import { getBackendErrorMessage } from '@/utils/errorHandler';

export type InvoiceStatus =
  | 'PENDING_COLLECTION'
  | 'RECEIVED_BY_RIDER'
  | 'RECEIVED_AT_HUB'
  | 'WAITING_CONFIRMATION'
  | 'DISPUTED'
  | 'RESOLVED'
  | 'EXCEPTION'
  | 'COLLECTED'
  | 'SUBMITTED_TO_CLIENT'
  | 'CLIENT_ACKNOWLEDGED';

export type AgingTier = 'green' | 'amber' | 'orange' | 'red';

export type ExceptionReason =
  | 'OUTLET_REFUSED'
  | 'DOC_LOST'
  | 'RIDER_CANNOT_RETURN'
  | 'OTHER';

export type DisputeReason =
  | 'MISSING_STAMP'
  | 'MISSING_SIGNATURE'
  | 'WRONG_OUTLET_NAME'
  | 'WRONG_ITEMS'
  | 'ILLEGIBLE'
  | 'DAMAGED'
  | 'OTHER';

export interface EntityRef {
  id: string;
  name: string;
}

export interface OutletRef {
  id?: string | null;
  name: string;
  address?: string | null;
}

export interface HubRef {
  id?: string | null;
  name: string;
  address?: string | null;
}

export interface Invoice {
  id: string;
  invoiceNumber: string;
  airwaybill: string;
  /** "No AWB" on the Delivery Note = delivery provider order ID. */
  bookingId: string | null;
  batchId: string | null;
  rider: EntityRef | null;
  client: EntityRef;
  outlet: OutletRef | null;
  hub: HubRef | null;
  itemsCount: number;
  issueDate: string;
  dueDate: string | null;
  status: InvoiceStatus;
  returnedAt: string | null;
  notes: string | null;
  /** Accumulated outlet-exception visit-evidence photos across all of this
   *  invoice's exceptions (each with its own exceptionCode + capture time);
   *  null when none. */
  exceptionEvidence: ExceptionEvidencePhoto[] | null;
  daysSince: number | null;
  agingTier: AgingTier | null;
  createdAt: string;
  updatedAt: string;
}

export interface InvoiceSummary {
  totalToday: number;
  received: number;
  receivedByRider: number;
  pending: number;
  exception: number;
  redAlerts: number;
  awaitingConfirmation: number;
  disputed: number;
}

export interface InvoiceListParams {
  status?: InvoiceStatus | 'ALL';
  search?: string;
  clientId?: string;
  outletId?: string;
  hubId?: string;
  /** Filter to one rider (rider->>'id' = driver id). Powers the perf drawer + board redirect. */
  driverId?: string;
  agingTier?: string;
  issuedFrom?: string;
  issuedTo?: string;
  datePreset?: 'today' | 'yesterday' | 'last_7_days' | 'last_30_days';
  page?: number;
  pageSize?: number;
  sort?: string;
}

export interface InvoiceListResponse {
  items: Invoice[];
  page: number;
  pageSize: number;
  total: number;
}

export type BulkSubmitForConfirmErrorCode = 'NOT_FOUND' | 'INVALID_STATE';

export interface BulkSubmitForConfirmRowError {
  id: string;
  code: BulkSubmitForConfirmErrorCode;
  message: string;
}

export interface BulkSubmitForConfirmResult {
  updated: Invoice[];
  errors: BulkSubmitForConfirmRowError[];
}

export interface InvoiceLookupResponse {
  /** booking_id (Delivery Note grouping key) when the code resolved to a group;
   *  null for a legacy single invoice with no booking_id. */
  bookingId: string | null;
  /** True when the primary (first) invoice was already received. */
  alreadyReceived: boolean;
  /** Primary invoice — the first of the group (backward-compat single-invoice
   *  contract). */
  invoice: Invoice;
  /** Every invoice under the scanned Delivery Note. One entry for a single /
   *  legacy invoice; many when a booking_id resolves. Optional so an older
   *  backend (no `invoices` field) still parses — callers fall back to
   *  `[invoice]`. */
  invoices?: Invoice[];
}

export interface InvoiceImage {
  imageId: string;
  imageUrl: string;
  documentType: string;
}

export interface InvoiceImages {
  images: InvoiceImage[];
  spType: string | null;
}

/** One outlet-exception visit-evidence photo. Photos accumulate on the invoice
 *  across exception events, so each carries its own `exceptionCode` and
 *  `capturedAt` (when that exception was recorded, ISO 8601). */
export interface ExceptionEvidencePhoto {
  imageId: string;
  imageUrl: string;
  exceptionCode: string;
  capturedAt: string;
}

export interface InvoiceHistoryEntry {
  id: string;
  fromStatus: InvoiceStatus | null;
  toStatus: InvoiceStatus;
  changedAt: string;
  changedBy: { id: string; name: string; role: string };
  note: string | null;
  invoiceImages: InvoiceImages | null;
}

export interface InvoiceDetailResponse {
  invoice: Invoice;
  /** Ordered ASC (oldest first) — the detail panel timeline reads top-to-bottom. */
  history: InvoiceHistoryEntry[];
  related: null;
}

export type ExportScanPreset =
  | 'today'
  | 'yesterday'
  | 'last_7_days'
  | 'last_30_days';

// Export can be filtered by any invoice status.
export type InvoiceExportStatus = InvoiceStatus;

export type InvoiceExportFormat = 'csv' | 'xlsx';

export interface InvoiceExportParams {
  scannedPreset?: ExportScanPreset;
  scannedFrom?: string;
  scannedTo?: string;
  issueDatePreset?: ExportScanPreset;
  issueDateFrom?: string;
  issueDateTo?: string;
  clientId?: string;
  outletId?: string;
  hubId?: string;
  driverId?: string;
  status?: InvoiceExportStatus[];
  invoiceNumbers?: string[];
  format?: InvoiceExportFormat;
}

export interface RiderPerformanceParams {
  /** Hub identity (COALESCE(hub->>'id', hub->>'name')) — from the hub picker. */
  hubId: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface RiderPerformanceRow {
  /** rider->>'id' (= driver id); null for the "Unassigned" bucket. */
  driverId: string | null;
  driverName: string;
  total: number;
  /** Held (custody) — RECEIVED_BY_RIDER. */
  held: number;
  /** By rider — RECEIVED_BY_RIDER (identical to held, by design). */
  byRider: number;
  atHub: number;
  awaiting: number;
  exception: number;
  disputed: number;
  resolved: number;
  /** Needs rider action — EXCEPTION + RECEIVED_BY_RIDER (exception + held). */
  unresolvedByRider: number;
  red: number;
  oldestIssueDate: string | null;
  oldestDaysSince: number | null;
  avgAging: number;
}

export interface RiderPerformanceSummary {
  totalInvoice: number;
  /** Still with the rider — RECEIVED_BY_RIDER (= held). */
  atRider: number;
  receivedAtHub: number;
  awaitingConfirmation: number;
  exception: number;
  redAlerts: number;
}

export interface RiderPerformanceResponse {
  /** Query timestamp (ISO, Asia/Jakarta) — the "Data per…" stamp. */
  asOf: string;
  hubId: string;
  data: RiderPerformanceRow[];
  page: number;
  pageSize: number;
  /** Total rider groups for the hub (for pagination). */
  total: number;
}

export interface RiderBookingsParams {
  /** Rider id (rider->>'id'). */
  driverId: string;
  /** Hub identity — from the hub picker. */
  hubId: string;
  /** Substring over booking_id / invoice number / airwaybill. */
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface RiderBookingGroup {
  /** Delivery Note key. Null = the "No booking ID" bucket. */
  bookingId: string | null;
  invoiceCount: number;
  oldestIssueDate: string | null;
  oldestDaysSince: number | null;
  invoices: Invoice[];
}

export interface RiderBookingsResponse {
  hubId: string;
  driverId: string;
  data: RiderBookingGroup[];
  page: number;
  pageSize: number;
  /** Total booking groups for the rider+hub (for pagination). */
  total: number;
}

export interface InvoiceClientOption {
  id: string;
  name: string;
}

export interface InvoiceOutletOption {
  id: string;
  name: string;
  address: string | null;
}

export interface InvoiceHubOption {
  id: string;
  name: string;
  address: string | null;
}

export type ExportErrorCode =
  | 'EXPORT_LIMIT_EXCEEDED'
  | 'EXPORT_EMPTY'
  | 'VALIDATION_ERROR'
  | 'UNKNOWN';

export class InvoiceExportError extends Error {
  code: ExportErrorCode;
  count?: number;
  constructor(code: ExportErrorCode, message: string, count?: number) {
    super(message);
    this.code = code;
    this.count = count;
  }
}

export type ImportDuplicateMode = 'skip' | 'overwrite';

export interface ImportPreviewToCreateRow {
  rowNumber: number;
  invoiceNumber: string;
  airwaybill: string;
  issueDate: string;
  itemsCount: number;
}

export interface ImportPreviewDuplicateRow {
  rowNumber: number;
  invoiceNumber: string;
  csv: {
    airwaybill: string;
    issueDate: string;
    itemsCount: number;
  };
  existing: {
    airwaybill: string;
    issueDate: string;
    itemsCount: number;
    status: InvoiceStatus;
    returnedAt: string | null;
  };
}

export interface ImportPreviewError {
  rowNumber: number;
  field: string;
  value: string;
  message: string;
}

export interface ImportPreviewSummary {
  totalRows: number;
  toCreate: number;
  duplicates: number;
  errors: number;
  ignoredColumns: string[];
}

export interface ImportPreviewResult {
  summary: ImportPreviewSummary;
  toCreate: ImportPreviewToCreateRow[];
  duplicates: ImportPreviewDuplicateRow[];
  errors: ImportPreviewError[];
}

export interface ImportCommitResult {
  created: number;
  updated: number;
  skipped: number;
}

export type ImportErrorCode =
  | 'IMPORT_FILE_REQUIRED'
  | 'IMPORT_FILE_TOO_LARGE'
  | 'IMPORT_FILE_INVALID'
  | 'IMPORT_TOO_MANY_ROWS'
  | 'IMPORT_TOO_MANY_ERRORS'
  | 'IMPORT_CLIENT_NOT_FOUND'
  | 'IMPORT_VALIDATION_FAILED'
  | 'UNKNOWN';

export class InvoiceImportError extends Error {
  code: ImportErrorCode;
  preview?: ImportPreviewResult;
  constructor(
    code: ImportErrorCode,
    message: string,
    preview?: ImportPreviewResult
  ) {
    super(message);
    this.code = code;
    this.preview = preview;
  }
}

export const IMPORT_TEMPLATE_HEADER =
  'invoice_number,airwaybill,booking_id,items_count,issue_date,outlet_id,outlet_name,recipient_address,hub_id,hub_name,hub_address,rider_id,rider_name,batch_id,notes';

export const IMPORT_TEMPLATE_FILENAME = 'invoice-import-template.csv';

type Envelope<T> = { status: number; message?: string; data: T };

const unwrap = <T>(payload: Envelope<T> | T): T =>
  payload && typeof payload === 'object' && 'data' in (payload as any)
    ? (payload as Envelope<T>).data
    : (payload as T);

export const invoicesAPI = {
  list: async (
    params: InvoiceListParams = {}
  ): Promise<InvoiceListResponse> => {
    try {
      const response = await Axios.get('/v1/invoices', { params });
      return unwrap<InvoiceListResponse>(response.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  summary: async (): Promise<InvoiceSummary> => {
    try {
      const response = await Axios.get('/v1/invoices/summary');
      return unwrap<InvoiceSummary>(response.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  lookup: async (invoiceNumber: string): Promise<InvoiceLookupResponse> => {
    try {
      const response = await Axios.get('/v1/invoices/lookup', {
        params: { invoiceNumber },
      });
      return unwrap<InvoiceLookupResponse>(response.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  getById: async (id: string): Promise<Invoice> => {
    try {
      const response = await Axios.get(`/v1/invoices/${id}`);
      return unwrap<Invoice>(response.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  receive: async (id: string, note?: string | null): Promise<Invoice> => {
    try {
      const response = await Axios.post(`/v1/invoices/${id}/receive`, {
        note: note ?? null,
      });
      return unwrap<Invoice>(response.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  flagException: async (
    id: string,
    reason: ExceptionReason,
    note: string
  ): Promise<Invoice> => {
    try {
      const response = await Axios.post(`/v1/invoices/${id}/exception`, {
        reason,
        note,
      });
      return unwrap<Invoice>(response.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  reopen: async (id: string, note: string): Promise<Invoice> => {
    try {
      const response = await Axios.post(`/v1/invoices/${id}/reopen`, { note });
      return unwrap<Invoice>(response.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  submitForConfirm: async (
    id: string,
    note?: string | null
  ): Promise<Invoice> => {
    try {
      const response = await Axios.post(
        `/v1/invoices/${id}/submit-for-confirm`,
        { note: note ?? null }
      );
      return unwrap<Invoice>(response.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  bulkSubmitForConfirm: async (
    ids: string[],
    note?: string | null
  ): Promise<BulkSubmitForConfirmResult> => {
    try {
      const response = await Axios.post(
        '/v1/invoices/bulk/submit-for-confirm',
        { ids, note: note ?? null }
      );
      return unwrap<BulkSubmitForConfirmResult>(response.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  dispute: async (
    id: string,
    reason: DisputeReason,
    note: string
  ): Promise<Invoice> => {
    try {
      const response = await Axios.post(`/v1/invoices/${id}/dispute`, {
        reason,
        note,
      });
      return unwrap<Invoice>(response.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  resubmit: async (id: string, note: string): Promise<Invoice> => {
    try {
      const response = await Axios.post(`/v1/invoices/${id}/resubmit`, {
        note,
      });
      return unwrap<Invoice>(response.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  resolve: async (id: string, note?: string | null): Promise<Invoice> => {
    try {
      const response = await Axios.post(`/v1/invoices/${id}/resolve`, {
        note: note ?? null,
      });
      return unwrap<Invoice>(response.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  history: async (id: string): Promise<InvoiceHistoryEntry[]> => {
    try {
      const response = await Axios.get(`/v1/invoices/${id}/history`);
      const payload = unwrap<{ items: InvoiceHistoryEntry[] }>(response.data);
      return payload.items;
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  detail: async (id: string): Promise<InvoiceDetailResponse> => {
    try {
      const response = await Axios.get(`/v1/invoices/${id}/detail`);
      return unwrap<InvoiceDetailResponse>(response.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  clients: async (search?: string): Promise<InvoiceClientOption[]> => {
    try {
      const params = search?.trim() ? { search: search.trim() } : undefined;
      const response = await Axios.get('/v1/invoices/clients', { params });
      const payload = unwrap<{ items: InvoiceClientOption[] }>(response.data);
      return payload.items;
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  outlets: async (search?: string): Promise<InvoiceOutletOption[]> => {
    try {
      const params = search?.trim() ? { search: search.trim() } : undefined;
      const response = await Axios.get('/v1/invoices/outlets', { params });
      const payload = unwrap<{ items: InvoiceOutletOption[] }>(response.data);
      return payload.items;
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  hubs: async (search?: string): Promise<InvoiceHubOption[]> => {
    try {
      const params = search?.trim() ? { search: search.trim() } : undefined;
      const response = await Axios.get('/v1/invoices/hubs', { params });
      const payload = unwrap<{ items: InvoiceHubOption[] }>(response.data);
      return payload.items;
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  riderPerformance: async (
    params: RiderPerformanceParams
  ): Promise<RiderPerformanceResponse> => {
    try {
      const response = await Axios.get('/v1/invoices/performance/riders', {
        params,
      });
      return unwrap<RiderPerformanceResponse>(response.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  riderPerformanceSummary: async (
    hubId: string
  ): Promise<RiderPerformanceSummary> => {
    try {
      const response = await Axios.get('/v1/invoices/performance/summary', {
        params: { hubId },
      });
      return unwrap<RiderPerformanceSummary>(response.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  riderBookings: async (
    params: RiderBookingsParams
  ): Promise<RiderBookingsResponse> => {
    try {
      const response = await Axios.get('/v1/invoices/bookings', { params });
      return unwrap<RiderBookingsResponse>(response.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  importDryRun: async (params: {
    clientId: string;
    dueDate?: string | null;
    file: File;
  }): Promise<ImportPreviewResult> => {
    try {
      const form = new FormData();
      form.append('clientId', params.clientId);
      if (params.dueDate) form.append('dueDate', params.dueDate);
      form.append('file', params.file, params.file.name);
      const response = await Axios.post('/v1/invoices/import:dry-run', form, {
        headers: { 'Content-Type': undefined },
      });
      return unwrap<ImportPreviewResult>(response.data);
    } catch (error: any) {
      throw parseImportError(error);
    }
  },

  importCommit: async (params: {
    clientId: string;
    dueDate?: string | null;
    duplicateMode: ImportDuplicateMode;
    file: File;
  }): Promise<ImportCommitResult> => {
    try {
      const form = new FormData();
      form.append('clientId', params.clientId);
      if (params.dueDate) form.append('dueDate', params.dueDate);
      form.append('duplicateMode', params.duplicateMode);
      form.append('file', params.file, params.file.name);
      const response = await Axios.post('/v1/invoices/import:commit', form, {
        headers: { 'Content-Type': undefined },
      });
      return unwrap<ImportCommitResult>(response.data);
    } catch (error: any) {
      throw parseImportError(error);
    }
  },

  downloadTemplate: (): void => {
    const BOM = '﻿';
    const content = BOM + IMPORT_TEMPLATE_HEADER + '\r\n';
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = IMPORT_TEMPLATE_FILENAME;
    a.setAttribute('aria-label', 'Download CSV template');
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },

  export: async (params: InvoiceExportParams): Promise<void> => {
    try {
      // Serialize array params (status, invoiceNumbers) as a single
      // comma-joined string to keep the query short and to match the BE
      // splitter (it accepts both repeated and comma-joined forms).
      const serialized: Record<string, string | undefined> = {
        scannedPreset: params.scannedPreset,
        scannedFrom: params.scannedFrom,
        scannedTo: params.scannedTo,
        issueDatePreset: params.issueDatePreset,
        issueDateFrom: params.issueDateFrom,
        issueDateTo: params.issueDateTo,
        clientId: params.clientId,
        outletId: params.outletId,
        hubId: params.hubId,
        driverId: params.driverId,
        status:
          params.status && params.status.length > 0
            ? params.status.join(',')
            : undefined,
        invoiceNumbers:
          params.invoiceNumbers && params.invoiceNumbers.length > 0
            ? params.invoiceNumbers.join(',')
            : undefined,
        format: params.format,
      };
      const response = await Axios.get('/v1/invoices/export', {
        params: serialized,
        responseType: 'blob',
      });
      const blob = response.data as Blob;
      const disposition =
        response.headers['content-disposition'] ||
        response.headers['Content-Disposition'] ||
        '';
      const match = /filename="?([^";]+)"?/i.exec(disposition);
      const fallbackName =
        params.format === 'xlsx'
          ? 'received-invoices.xlsx'
          : 'received-invoices.csv';
      const filename = match?.[1] || fallbackName;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (error: any) {
      const parsed = await parseExportError(error);
      throw parsed;
    }
  },
};

const parseImportError = (error: any): InvoiceImportError => {
  const data = error?.response?.data;
  const code = (data?.error as ImportErrorCode) || 'UNKNOWN';
  const message =
    (data?.message as string) ||
    (typeof data?.error === 'string' ? data.error : null) ||
    getBackendErrorMessage(error);
  const preview =
    data && typeof data === 'object' && data.data && data.data.summary
      ? (data.data as ImportPreviewResult)
      : undefined;
  return new InvoiceImportError(code, message, preview);
};

const parseExportError = async (error: any): Promise<InvoiceExportError> => {
  const data = error?.response?.data;
  let payload: any = data;
  if (data instanceof Blob) {
    try {
      payload = JSON.parse(await data.text());
    } catch {
      payload = null;
    }
  }
  // The API error envelope is `{ status: 'Failed', error: <message> }` (see the
  // backend HttpExceptionFilter) — so the human-readable text lives in `error`,
  // not `message`. Some paths use `message` instead, and Nest validation errors
  // send `message` as a string[]. Normalize any of them to a readable sentence.
  const flatten = (v: unknown): string | undefined => {
    if (Array.isArray(v)) {
      const joined = v.filter(Boolean).join(' ').trim();
      return joined || undefined;
    }
    return typeof v === 'string' && v.trim() ? v : undefined;
  };
  const KNOWN_CODES: ExportErrorCode[] = [
    'EXPORT_LIMIT_EXCEEDED',
    'EXPORT_EMPTY',
    'VALIDATION_ERROR',
  ];
  const rawError = payload?.error;
  const code: ExportErrorCode =
    typeof rawError === 'string' &&
    KNOWN_CODES.includes(rawError as ExportErrorCode)
      ? (rawError as ExportErrorCode)
      : 'UNKNOWN';
  // Prefer an explicit `message`; otherwise use `error` when it's a real message
  // (not one of the codes above); finally fall back to the axios status string.
  const message =
    flatten(payload?.message) ??
    (code === 'UNKNOWN' ? flatten(rawError) : undefined) ??
    getBackendErrorMessage(error);
  const count = typeof payload?.count === 'number' ? payload.count : undefined;
  return new InvoiceExportError(code, message, count);
};

export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  PENDING_COLLECTION: 'Pending',
  RECEIVED_BY_RIDER: 'Received by Rider',
  RECEIVED_AT_HUB: 'Received at Hub',
  WAITING_CONFIRMATION: 'Awaiting confirm',
  DISPUTED: 'Disputed',
  RESOLVED: 'Resolved',
  EXCEPTION: 'Exception',
  COLLECTED: 'Collected',
  SUBMITTED_TO_CLIENT: 'Submitted',
  CLIENT_ACKNOWLEDGED: 'Acknowledged',
};

export const EXCEPTION_REASON_LABELS: Record<ExceptionReason, string> = {
  OUTLET_REFUSED: 'Outlet refused',
  DOC_LOST: 'Document lost',
  RIDER_CANNOT_RETURN: 'Rider cannot return',
  OTHER: 'Other',
};

export const DISPUTE_REASON_LABELS: Record<DisputeReason, string> = {
  MISSING_STAMP: 'Missing stamp',
  MISSING_SIGNATURE: 'Missing signature',
  WRONG_OUTLET_NAME: 'Wrong outlet name',
  WRONG_ITEMS: 'Wrong items',
  ILLEGIBLE: 'Illegible',
  DAMAGED: 'Damaged',
  OTHER: 'Other',
};
